import React from 'react';

import noop from 'lodash/noop';

import { mdiHome, mdiMagnify } from '@lumapps/lumx/icons';
import { IconSizes, Size } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { InfiniteScroll } from '@lumapps/utils/hooks/useInfiniteScroll';
import { EditableListActionsAndStates, EditableListSearchable } from '@lumapps/utils/types/EditableList';

import { EmptyState, EmptyStateProps } from '../EmptyState';
import { NoResultsState, NoResultsStateProps } from '../NoResultsState';
import { ServiceNotAvailableState, ServiceNotAvailableStateProps } from '../ServiceNotAvailableState';

export interface ListStateProps<T> extends EditableListActionsAndStates, EditableListSearchable {
    entities?: T[];
    /** empty state props */
    emptyStateProps?: Partial<EmptyStateProps>;
    /** filtered state props */
    filteredStateProps?: Partial<NoResultsStateProps>;
    /** additional service not available props */
    serviceNotAvailableProps?: Partial<ServiceNotAvailableStateProps>;
    /** default to true - whether to display or not the empty state */
    displayEmptyState?: boolean;
    /** general icon size for all states */
    iconSize?: IconSizes;
}

/**
 * Component that groups multiple state components that are normally used when displaying lists
 * @param ListState
 * @returns ListStateProps
 */
export const ListState = <T,>({
    entities = [],
    hasError = false,
    isLoading = false,
    isFiltered,
    searchValue,
    iconSize = Size.xl,
    emptyStateProps = {},
    serviceNotAvailableProps = {},
    filteredStateProps,
    onLoadMore,
    displayEmptyState = true,
}: ListStateProps<T>) => {
    const translations = useTranslate();
    const { translateKey } = translations;
    const areEntitiesFiltered = (isFiltered && isFiltered !== undefined) || Boolean(searchValue);
    const areThereNoEntities = entities && entities.length === 0 && !hasError && !isLoading;

    return (
        <>
            {displayEmptyState && areThereNoEntities && !areEntitiesFiltered && !hasError ? (
                <EmptyState
                    title={translateKey(areEntitiesFiltered ? GLOBAL.NO_RESULTS_FOUND : GLOBAL.NO_RESULTS)}
                    iconProps={{ icon: areEntitiesFiltered ? mdiMagnify : mdiHome, size: iconSize }}
                    message={areEntitiesFiltered ? translateKey(GLOBAL.TRY_ANOTHER_QUERY_OR_FILTER) : undefined}
                    isVerticallyCentered
                    {...emptyStateProps}
                    wrapperProps={{
                        style: {
                            // In order to display the empty state centered on the page
                            // we need to force its height to 100% since the prop `isVerticallyCentered`
                            // only works when the parent has a height of 100%
                            height: '100%',
                            ...emptyStateProps?.wrapperProps?.style,
                        },
                        ...emptyStateProps?.wrapperProps,
                    }}
                />
            ) : null}

            {displayEmptyState && areThereNoEntities && areEntitiesFiltered && !hasError ? (
                <NoResultsState
                    iconProps={{ icon: mdiMagnify, size: iconSize }}
                    isVerticallyCentered
                    title={isFiltered || isFiltered === undefined ? translateKey(GLOBAL.NO_RESULTS_FOUND) : undefined}
                    message={
                        isFiltered || isFiltered === undefined
                            ? translateKey(GLOBAL.TRY_ANOTHER_QUERY_OR_FILTER)
                            : undefined
                    }
                    searchValue={searchValue}
                    {...filteredStateProps}
                    wrapperProps={{
                        style: {
                            // In order to display the empty state centered on the page
                            // we need to force its height to 100% since the prop `isVerticallyCentered`
                            // only works when the parent has a height of 100%
                            height: '100%',
                            ...filteredStateProps?.wrapperProps?.style,
                        },
                        ...filteredStateProps?.wrapperProps,
                    }}
                />
            ) : null}

            {hasError ? (
                <ServiceNotAvailableState
                    shouldShowIcon
                    isVerticallyCentered
                    iconSize={iconSize}
                    {...serviceNotAvailableProps}
                    wrapperProps={{
                        style: {
                            // In order to display the service not available state centered on the page
                            // we need to force its height to 100% since the prop `isVerticallyCentered`
                            // only works when the parent has a height of 100%
                            height: '100%',
                            ...serviceNotAvailableProps?.wrapperProps?.style,
                        },
                        ...serviceNotAvailableProps?.wrapperProps,
                    }}
                />
            ) : null}

            {onLoadMore && <InfiniteScroll callback={isLoading ? noop : onLoadMore} />}
        </>
    );
};
